import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const box = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setRes(data); setOpen(true);
      } catch { setRes(null); } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); setRes(null); navigate(path); };

  const groups = res ? [
    ["Jobs", res.jobs, (j) => `${j.reference || "—"} · ${j.applicant_name || ""}`, (j) => `/app/jobs?edit=${j.id}`, (j) => j.bank_name || ""],
    ["Clients", res.clients, (c) => c.name, (c) => `/app/clients/${c.id}`, (c) => c.city || c.phone || ""],
    ["Invoices", res.invoices, (i) => i.invoice_number, () => `/app/invoices`, (i) => i.bill_to_name || ""],
    ["Branches", res.branches, (b) => `${b.bank_name || ""} — ${b.branch_name || ""}`, () => `/app/branches`, (b) => b.city || ""],
  ].filter(([, arr]) => (arr || []).length) : [];
  const total = groups.reduce((n, [, a]) => n + (a || []).length, 0);

  return (
    <div ref={box} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => res && setOpen(true)}
          placeholder="Search clients, jobs, invoices…"
          data-testid="global-search-input"
          className="w-full h-9 pl-9 pr-8 rounded-full border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/40"
        />
        {q && (
          <button onClick={() => { setQ(""); setRes(null); }} data-testid="global-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary">
            <X size={14} />
          </button>
        )}
      </div>
      {open && res && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-white shadow-xl max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {total === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">{loading ? "Searching…" : "No matches"}</div>
          ) : groups.map(([label, arr, title, to, sub]) => (
            <div key={label} className="py-1">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
              {arr.map((it) => (
                <button
                  key={it.id}
                  onClick={() => go(to(it))}
                  data-testid={`gs-${label.toLowerCase()}-${it.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between gap-2"
                >
                  <span className="text-sm text-primary truncate">{title(it)}</span>
                  <span className="text-[11px] text-muted-foreground truncate shrink-0">{sub(it)}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
